import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { useNavigate, Link } from "react-router-dom";

import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Users, DollarSign, Plus, Star, Building2, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/shared/PageHero";

interface EventOrganizer {
  id: string;
  name: string;
  company_name: string | null;
  category: string;
  description: string | null;
  image: string | null;
  total_events: number;
  rating: number;
  featured: boolean;
  contact_info: {
    email?: string;
    phone?: string;
  } | null;
  sponsorship_packages: Array<{
    name: string;
    price: number;
    benefits: string[];
  }> | null;
}

const EventsSponsors = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("browse");
  const [events, setEvents] = useState<EventOrganizer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApprovedEvents = async () => {
      try {
        const { data, error } = await supabase
          .from('event_organizers')
          .select('*')
          .eq('approval_status', 'approved')
          .order('featured', { ascending: false })
          .order('rating', { ascending: false });

        if (error) throw error;

        const mappedEvents: EventOrganizer[] = (data || []).map((event) => ({
          id: event.id,
          name: event.name,
          company_name: event.company_name,
          category: event.category,
          description: event.description,
          image: event.image,
          total_events: event.total_events,
          rating: event.rating,
          featured: event.featured,
          contact_info: event.contact_info as EventOrganizer['contact_info'],
          sponsorship_packages: event.sponsorship_packages as EventOrganizer['sponsorship_packages'],
        }));

        setEvents(mappedEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApprovedEvents();
  }, []);

  const EventCard = ({ event }: { event: EventOrganizer }) => {
    const lowestPackagePrice = event.sponsorship_packages?.length 
      ? Math.min(...event.sponsorship_packages.map(p => p.price))
      : null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
          <div className="relative">
            <img 
              src={event.image || '/placeholder.svg'} 
              alt={event.name}
              className="w-full h-48 object-cover"
            />
            {event.featured && (
              <Badge className="absolute top-2 right-2 bg-primary">
                <Star className="w-3 h-3 mr-1" />
                {language === 'ar' ? 'مميز' : 'Featured'}
              </Badge>
            )}
          </div>
          <CardHeader>
            <div className="flex justify-between items-start">
              <CardTitle className="text-lg">{event.name}</CardTitle>
              <Badge variant="secondary">{event.category}</Badge>
            </div>
            {event.company_name && (
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {event.company_name}
              </p>
            )}
            <CardDescription className="line-clamp-2">{event.description}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="space-y-2 mb-4 flex-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                {event.total_events} {language === 'ar' ? 'حدث' : 'events'}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Star className="w-4 h-4 text-yellow-500" />
                {event.rating.toFixed(1)}
              </div>
              {lowestPackagePrice && (
                <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <DollarSign className="w-4 h-4" />
                  {language === 'ar' ? 'تبدأ من' : 'Starting from'} {lowestPackagePrice.toLocaleString()} SAR
                </div>
              )}
              {event.sponsorship_packages && event.sponsorship_packages.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  {event.sponsorship_packages.length} {language === 'ar' ? 'حزم رعاية' : 'sponsorship packages'}
                </div>
              )}
            </div>
            <Button className="w-full" asChild>
              <Link to={`/events/${event.id}`}>
                {language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className={`relative min-h-screen ${language === 'ar' ? 'text-right' : 'text-left'}`}>
      <SEO
        title="Events & Sponsors — Fusion Media"
        description="Discover events seeking sponsorship and book tier-based packages in Saudi Arabia through the Fusion Media platform."
      />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHero
          eyebrow={language === 'ar' ? 'منصة الأحداث والرعاية' : 'Events & Sponsorship Platform'}
          title={language === 'ar' ? 'الأحداث' : 'Events &'}
          accent={language === 'ar' ? 'والرعاة' : 'Sponsors'}
          subtitle={
            language === 'ar'
              ? 'منصة شاملة لإنشاء قوائم الأحداث والعثور على فرص الرعاية المثالية'
              : 'Your comprehensive platform to create event listings and find perfect sponsorship opportunities.'
          }
          centered
          ctas={[
            { label: language === 'ar' ? 'تصفح الفعاليات' : 'Browse Events', onClick: () => setActiveTab('browse'), icon: <Users className="w-4 h-4" /> },
            { label: language === 'ar' ? 'أضف فعاليتك' : 'List Your Event', variant: 'secondary', onClick: () => navigate('/event-listing-signup'), icon: <Building2 className="w-4 h-4" /> },
          ]}
        />

        {/* Content Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-8">
            <h2 className="text-2xl font-bold">
              {language === 'ar' ? 'الأحداث المتاحة للرعاية' : 'Events Seeking Sponsorship'}
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16 px-4 rounded-xl border border-primary/10 bg-primary/5">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">
                {language === 'ar' ? 'لا توجد فعاليات متاحة حالياً' : 'No events available yet'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {language === 'ar' 
                  ? 'كن أول من يضيف فعالية! أضف فعاليتك الآن للوصول إلى الرعاة'
                  : 'Be the first to list an event! Add your event now to reach sponsors'}
              </p>
              <Button onClick={() => navigate('/event-listing-signup')}>
                <Plus className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'أضف فعاليتك' : 'List Your Event'}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default EventsSponsors;
